import React, { useEffect, useState } from 'react';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  Shield as ShieldIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { sendRequest } from '../../utils/dateHelpers';
import { ROLE_NAMES, ROLES } from '../../constants/rolePermissions';
import EmptyState from '../Common/EmptyState';
import Loading from '../Loading';

const API_GET_USERS_URL = '/settings/users/get';
const API_UPDATE_USER_URL = '/settings/users/update';
const API_DELETE_USER_URL = '/settings/users/delete';
const API_SEND_RESET_PASSWORD_URL = '/settings/users/send-reset-password';

const formatDate = value => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
};

const normalizeUser = user => ({
  id: user?._id || user?.id || '',
  username: user?.username || '',
  email: user?.email || user?.username || '',
  fullName: user?.full_name || user?.fullName || '',
  role: user?.role || ROLES.VIEWER,
  createdAt: user?.created_at || user?.createdAt || ''
});

const ManageUserAccess = () => {
  const { userInfo } = useSelector(state => state);
  const currentUserId = userInfo?.user?._id || userInfo?.user?.uid || '';
  const currentUserEmail = userInfo?.user?.email || '';
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingUserId, setEditingUserId] = useState('');
  const [savingUserId, setSavingUserId] = useState('');
  const [deletingUserId, setDeletingUserId] = useState('');
  const [resettingUserId, setResettingUserId] = useState('');
  const [editForm, setEditForm] = useState({
    fullName: '',
    role: ROLES.VIEWER
  });

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await sendRequest(API_GET_USERS_URL, 'GET');
      const userList = response?.data || response || [];
      const normalizedUsers = Array.isArray(userList)
        ? userList.map(normalizeUser)
        : [];

      setUsers(normalizedUsers);
      setFilteredUsers(normalizedUsers);
    } catch (error) {
      toast.error(error?.message || error || 'Failed to load users');
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      setFilteredUsers(users);
      return;
    }

    const result = users.filter(user =>
      [
        user.fullName,
        user.email,
        user.username,
        ROLE_NAMES[user.role] || user.role
      ]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(query))
    );

    setFilteredUsers(result);
  }, [search, users]);

  const startEdit = user => {
    setEditingUserId(user.id);
    setEditForm({
      fullName: user.fullName,
      role: user.role
    });
  };

  const cancelEdit = () => {
    setEditingUserId('');
    setEditForm({
      fullName: '',
      role: ROLES.VIEWER
    });
  };

  const handleEditChange = e => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async user => {
    if (!editForm.fullName.trim()) {
      toast.error('Full name is required');
      return;
    }

    try {
      setSavingUserId(user.id);

      const payload = {
        userId: user.id,
        full_name: editForm.fullName.trim(),
        role: editForm.role
      };

      const response = await sendRequest(API_UPDATE_USER_URL, 'POST', payload);

      if (response?.success !== false) {
        const updatedUsers = users.map(item =>
          item.id === user.id
            ? {
                ...item,
                fullName: editForm.fullName.trim(),
                role: editForm.role
              }
            : item
        );

        setUsers(updatedUsers);
        setEditingUserId('');
        toast.success('User updated successfully');
      }
    } catch (error) {
      toast.error(error?.message || error || 'Failed to update user');
    } finally {
      setSavingUserId('');
    }
  };

  const handleDelete = async user => {
    const isCurrentUser =
      user.id === currentUserId ||
      user.email.toLowerCase() === currentUserEmail.toLowerCase();

    if (isCurrentUser) {
      toast.error('You cannot delete your own account');
      return;
    }

    const confirmed = window.confirm(
      `Delete ${user.fullName || user.email}? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingUserId(user.id);

      const response = await sendRequest(API_DELETE_USER_URL, 'DELETE', {
        userId: user.id
      });

      if (response?.success !== false) {
        const updatedUsers = users.filter(item => item.id !== user.id);
        setUsers(updatedUsers);
        toast.success('User deleted successfully');
      }
    } catch (error) {
      toast.error(error?.message || error || 'Failed to delete user');
    } finally {
      setDeletingUserId('');
    }
  };

  const handleSendResetEmail = async user => {
    try {
      setResettingUserId(user.id);

      const response = await sendRequest(API_SEND_RESET_PASSWORD_URL, 'POST', {
        userId: user.id
      });

      if (response?.success !== false) {
        toast.success('Reset password email sent successfully');
      }
    } catch (error) {
      toast.error(
        error?.message || error || 'Failed to send reset password email'
      );
    } finally {
      setResettingUserId('');
    }
  };

  return (
    <div className="mx-auto max-w-270">
      <div
        className={`rounded-sm border shadow-default ${
          isDarkMode ? 'border-strokedark bg-boxdark' : 'border-stroke bg-white'
        }`}
      >
        <div
          className={`border-b px-6 py-5 ${
            isDarkMode ? 'border-strokedark' : 'border-stroke'
          }`}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full max-w-xs">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <SearchIcon
                  className={`h-5 w-5 ${
                    isDarkMode ? 'text-slate-500' : 'text-slate-400'
                  }`}
                />
              </div>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search users..."
                className={`w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${
                  isDarkMode
                    ? 'border-strokedark bg-meta-4 text-slate-100 placeholder:text-slate-500'
                    : 'border-slate-300 bg-white text-slate-900'
                }`}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div
              className={`rounded-sm border px-4 py-4 ${
                isDarkMode
                  ? 'border-strokedark bg-meta-4'
                  : 'border-slate-200 bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-sm ${
                    isDarkMode
                      ? 'bg-blue-500/15 text-blue-300'
                      : 'bg-blue-100 text-blue-600'
                  }`}
                >
                  <GroupIcon />
                </div>
                <div>
                  <p
                    className={`text-xs uppercase tracking-wider ${
                      isDarkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    Total Users
                  </p>
                  <p
                    className={`text-lg font-semibold ${
                      isDarkMode ? 'text-white' : 'text-slate-900'
                    }`}
                  >
                    {users.length}
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`rounded-sm border px-4 py-4 ${
                isDarkMode
                  ? 'border-strokedark bg-meta-4'
                  : 'border-slate-200 bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-sm ${
                    isDarkMode
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : 'bg-emerald-100 text-emerald-600'
                  }`}
                >
                  <ShieldIcon />
                </div>
                <div>
                  <p
                    className={`text-xs uppercase tracking-wider ${
                      isDarkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    Account Admins
                  </p>
                  <p
                    className={`text-lg font-semibold ${
                      isDarkMode ? 'text-white' : 'text-slate-900'
                    }`}
                  >
                    {
                      users.filter(user => user.role === ROLES.ACCOUNT_ADMIN)
                        .length
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`overflow-hidden border ${
              isDarkMode ? 'border-strokedark' : 'border-slate-200'
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px]">
                <thead className={isDarkMode ? 'bg-meta-4' : 'bg-slate-50'}>
                  <tr
                    className={`divide-x ${
                      isDarkMode ? 'divide-strokedark' : 'divide-slate-200'
                    }`}
                  >
                    <th
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      Full Name
                    </th>
                    <th
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      Email
                    </th>
                    <th
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      Username
                    </th>
                    <th
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      Role
                    </th>
                    <th
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      Created
                    </th>
                    <th
                      className={`w-[260px] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`divide-y ${
                    isDarkMode
                      ? 'divide-strokedark bg-boxdark'
                      : 'divide-slate-200 bg-white'
                  }`}
                >
                  {loading ? (
                    [...Array(5)].map((_, index) => (
                      <tr key={index} className="animate-pulse">
                        <td className="px-4 py-4">
                          <div
                            className={`h-4 w-32 ${
                              isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                            }`}
                          ></div>
                        </td>
                        <td className="px-4 py-4">
                          <div
                            className={`h-4 w-40 ${
                              isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                            }`}
                          ></div>
                        </td>
                        <td className="px-4 py-4">
                          <div
                            className={`h-4 w-40 ${
                              isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                            }`}
                          ></div>
                        </td>
                        <td className="px-4 py-4">
                          <div
                            className={`h-4 w-24 ${
                              isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                            }`}
                          ></div>
                        </td>
                        <td className="px-4 py-4">
                          <div
                            className={`h-4 w-24 ${
                              isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                            }`}
                          ></div>
                        </td>
                        <td className="px-4 py-4">
                          <div
                            className={`ml-auto h-8 w-48 ${
                              isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                            }`}
                          ></div>
                        </td>
                      </tr>
                    ))
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8">
                        <EmptyState
                          compact
                          title="No Data Available"
                          description={
                            search
                              ? 'No users matched your current search.'
                              : 'There are no user access records to display yet.'
                          }
                        />
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map(user => {
                      const isEditing = editingUserId === user.id;
                      const isSaving = savingUserId === user.id;
                      const isDeleting = deletingUserId === user.id;
                      const isResetting = resettingUserId === user.id;
                      const isCurrentUser =
                        user.id === currentUserId ||
                        user.email.toLowerCase() ===
                          currentUserEmail.toLowerCase();

                      return (
                        <tr
                          key={user.id}
                          className={`divide-x ${
                            isDarkMode
                              ? 'divide-strokedark'
                              : 'divide-slate-200'
                          }`}
                        >
                          <td className="px-4 py-4">
                            {isEditing ? (
                              <input
                                type="text"
                                name="fullName"
                                value={editForm.fullName}
                                onChange={handleEditChange}
                                className={`w-full border px-3 py-2 text-sm outline-none focus:border-blue-500 ${
                                  isDarkMode
                                    ? 'border-strokedark bg-meta-4 text-slate-100'
                                    : 'border-slate-300 text-slate-900'
                                }`}
                              />
                            ) : (
                              <div
                                className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
                              >
                                {user.fullName || '-'}
                              </div>
                            )}
                          </td>
                          <td
                            className={`px-4 py-4 text-sm ${
                              isDarkMode ? 'text-slate-300' : 'text-slate-600'
                            }`}
                          >
                            {user.email || '-'}
                          </td>
                          <td
                            className={`px-4 py-4 text-sm ${
                              isDarkMode ? 'text-slate-300' : 'text-slate-600'
                            }`}
                          >
                            {user.username || '-'}
                          </td>
                          <td className="px-4 py-4">
                            {isEditing ? (
                              <select
                                name="role"
                                value={editForm.role}
                                onChange={handleEditChange}
                                className={`w-full border px-3 py-2 text-sm outline-none focus:border-blue-500 ${
                                  isDarkMode
                                    ? 'border-strokedark bg-meta-4 text-slate-100'
                                    : 'border-slate-300 text-slate-900'
                                }`}
                              >
                                {[
                                  ROLES.ACCOUNT_ADMIN,
                                  ROLES.PANEL_ADMIN,
                                  ROLES.VIEWER
                                ].map(role => (
                                  <option key={role} value={role}>
                                    {ROLE_NAMES[role]}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span
                                className={`inline-flex px-2.5 py-1 text-xs font-medium ${
                                  isDarkMode
                                    ? 'bg-slate-800 text-slate-200'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {ROLE_NAMES[user.role] || user.role}
                              </span>
                            )}
                          </td>
                          <td
                            className={`px-4 py-4 text-sm ${
                              isDarkMode ? 'text-slate-300' : 'text-slate-600'
                            }`}
                          >
                            {formatDate(user.createdAt)}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex min-w-[220px] justify-start">
                              {isEditing ? (
                                <div
                                  className={`flex w-full flex-wrap items-center gap-2 rounded-2xl border p-2 ${
                                    isDarkMode
                                      ? 'border-strokedark bg-meta-4'
                                      : 'border-slate-200 bg-slate-50'
                                  }`}
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleSave(user)}
                                    disabled={isSaving}
                                    className="inline-flex min-w-[96px] items-center justify-center gap-1 rounded-xl bg-slate-900 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                                  >
                                    <SaveIcon fontSize="inherit" />
                                    {isSaving ? 'Saving' : 'Save'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelEdit}
                                    disabled={isSaving}
                                    className={`inline-flex min-w-[96px] items-center justify-center gap-1 rounded-xl border px-3.5 py-2 text-xs font-semibold transition ${
                                      isDarkMode
                                        ? 'border-strokedark bg-boxdark text-slate-200 hover:bg-slate-800'
                                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-100'
                                    }`}
                                  >
                                    <CloseIcon fontSize="inherit" />
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div
                                  className={`flex w-full flex-col gap-2 rounded-2xl border p-2 shadow-sm ${
                                    isDarkMode
                                      ? 'border-strokedark bg-boxdark-2'
                                      : 'border-slate-200 bg-gradient-to-br from-slate-50 to-white'
                                  }`}
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleSendResetEmail(user)}
                                    disabled={isResetting}
                                    className={`inline-flex w-full items-center justify-center gap-2 rounded-xl border px-3.5 py-2.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                      isDarkMode
                                        ? 'border-strokedark bg-boxdark text-slate-200 hover:border-blue-700 hover:bg-blue-950/40 hover:text-blue-300'
                                        : 'border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
                                    }`}
                                  >
                                    <EmailIcon fontSize="inherit" />
                                    {isResetting ? (
                                      <Loading type="inline" />
                                    ) : (
                                      'Send Reset Email'
                                    )}
                                  </button>
                                  <div className="grid w-full grid-cols-2 gap-2">
                                    <button
                                      type="button"
                                      onClick={() => startEdit(user)}
                                      className={`inline-flex items-center justify-center gap-1 rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${
                                        isDarkMode
                                          ? 'border-strokedark bg-boxdark text-slate-200 hover:border-slate-600 hover:bg-slate-800'
                                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-100'
                                      }`}
                                    >
                                      <EditIcon fontSize="inherit" />
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDelete(user)}
                                      disabled={isDeleting || isCurrentUser}
                                      className="inline-flex items-center justify-center gap-1 rounded-xl bg-rose-500 px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:bg-rose-300"
                                    >
                                      <DeleteIcon fontSize="inherit" />
                                      {isDeleting ? 'Deleting' : 'Delete'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageUserAccess;
